'use client';

/**
 * Repeatable editorial block: image + rich-text body, with an optional header slot.
 * Pass `index` to get automatic left/right image alternation and LCP priority on the first block.
 * Pass `heroContent` to render the default HeroContent heading; use `header` to override with custom JSX.
 */
import { extractFirstHeading } from '@lib/util/typography';
import EditorialSection from '@modules/common/components/editorial-section';
import MarkdownContent from '@modules/common/components/markdown-content';
import HeroContent, { type HeroContentProps } from './hero-content';

type EditorialBlockProps = {
  image: { src: string; alt: string };
  body: string;
  index: number;
  headingId: string;
  heroContent?: Omit<HeroContentProps, 'headingId'>;
  header?: React.ReactNode;
};

export default function EditorialBlock({
  image,
  body,
  index,
  headingId,
  heroContent,
  header,
}: EditorialBlockProps) {
  const isFirst = index === 0;
  const sectionLabel = extractFirstHeading(body);

  const renderedHeader =
    header ??
    (heroContent ? (
      <HeroContent headingId={headingId} {...heroContent} />
    ) : (
      <h2 id={headingId} className="sr-only">
        {sectionLabel}
      </h2>
    ));

  return (
    <EditorialSection
      image={image}
      headingId={headingId}
      isImageRight={index % 2 !== 0}
      isPriority={isFirst}
    >
      {renderedHeader}
      <MarkdownContent
        content={body}
        className="prose-headings:mb-2 prose-headings:text-brand-neutral-900 prose-p:text-brand-neutral-900 prose-headings:font-light!"
      />
    </EditorialSection>
  );
}
